import type { GameId, SlotId } from './common';
import type { GameSection } from './catalog';
import type { Mood } from './passport';
import type { CrashPhase } from './games/crash';
import type { DdzLobbyResponse } from './games/doudizhu';
import type { HongbaoPhase, HongbaoRoom } from './games/hongbao';
import type { MjtLobbyResponse } from './games/mahjong-table';
import type { PotPhase } from './games/pot';
import type { RouletteBetColor, RoulettePhase } from './games/roulette';

export type GameKind =
  | 'crash'
  | 'pot'
  | 'roulette'
  | 'slot'
  | 'ladder'
  | 'grid'
  | 'hongbao'
  | 'mahjong'
  | 'mahjong-table'
  | 'cards'
  | 'doudizhu'
  /** skill puzzles (tile-match, color-sort, daily-words): pool competitions + training */
  | 'puzzle'
  /** chess: pvp tables + training against a bot */
  | 'chess';

interface GameSummaryBase {
  id: GameId;
  kind: GameKind;
  /** lobby section (catalog.ts GAME_SECTION) */
  section: GameSection;
  /**
   * `live` once the game's server module is registered and the game is released (no `comingSoon` in GAME_CATALOG);
   * `soon` otherwise (lobby shows the card disabled)
   */
  status: 'live' | 'soon';
  /** stake bounds (house: one bet; pvp: the table stakes / reserve; pool: the entry fees) */
  minBet: number;
  maxBet: number;
  /** YYYY-MM-DD the game joined the catalog (passport.ts GAME_CATALOG): «New» for guests */
  addedAt: string;
  /** mood tags («Try another») */
  moods: Mood[];
  /** a free practice mode exists (no stake, no reward) */
  training: boolean;
}

/**
 * GET /api/games → { games: GameSummary[] }. Discriminated by `economy` (catalog.ts): only a game against the house
 * has a target `rtp`; games between people (`pvp`) and prize-pool competitions (`pool`) have the house fee `feeBps`
 * instead — never a made-up RTP.
 */
export type GameSummary =
  | (GameSummaryBase & {
      economy: 'house';
      /** target RTP in percent (e.g. 95) */
      rtp: number;
    })
  | (GameSummaryBase & { economy: 'pvp'; /** house fee of a won bank, basis points */ feeBps: number })
  | (GameSummaryBase & {
      economy: 'pool';
      /** house fee of the prize fund, basis points */
      feeBps: number;
      /** entry fees offered, tokens */
      entries: number[];
    });

/**
 * GET /api/lobby/live — everything the lobby shows as "live": the big-wins ticker and the previews of the shared
 * rounds. Every number is real; a block without data is `null` / absent (the lobby shows a neutral state, never
 * made-up activity). The server caches the whole response for a moment (`LOBBY_LIVE_CACHE_MS`).
 */
export interface LobbyLiveResponse {
  /** recent big wins, newest first */
  wins: LiveWin[];
  /** server epoch ms when the response was built — clients compute their clock offset from it */
  serverNow: number;
  /** null: Liftoff is not running / no round yet */
  crash: LobbyCrash | null;
  pot: LobbyPot | null;
  roulette: LobbyRoulette | null;
  /** Red Envelopes tables (null: not running / no round yet; optional for older servers) */
  hongbao?: LobbyHongbao | null;
  /** Mahjong Tables: queues and tables by stake (null: not running; optional for older servers) */
  mahjongTable?: MjtLobbyResponse | null;
  /** Dou Dizhu tables: queues and tables by tier (null: not running; optional for older servers) */
  doudizhu?: DdzLobbyResponse | null;
  /** slot → distinct players with a spin in the last `activeWindowMin` minutes (slots without spins: 0) */
  slots: Partial<Record<SlotId, number>>;
  activeWindowMin: number;
}

/** Liftoff round preview. Multiplier while flying: e^(growthPerMs · (now − startedAt)). */
export interface LobbyCrash {
  roundNo: number;
  phase: CrashPhase;
  bettingEndsAt: number;
  /** take-off, server ms; null while betting */
  startedAt: number | null;
  /** only once crashed */
  crashPoint: number | null;
  growthPerMs: number;
  /** distinct players with a bet in the round */
  players: number;
  /** tokens staked in the round */
  bets: number;
  /** distinct players who cashed out */
  cashed: number;
  /** last finished rounds, newest first (≤ LOBBY_HISTORY) */
  history: { roundNo: number; crashPoint: number }[];
}

export interface LobbyPot {
  roundNo: number;
  phase: PotPhase;
  players: number;
  capacity: number;
  bank: number;
  /** server ms of the start; null while waiting for players / not open */
  countdownEndsAt: number | null;
  /** biggest stakes first (≤ 5); the house bot: `bot: true`, empty nickname */
  top: { nickname: string; stake: number; bot?: true }[];
}

/**
 * Red Envelopes: one row per stake that has a current round, in stake order (18, 88, 888). A stake may have several
 * tables: `players` / `capacity` are summed over them, `roundNo` / `phase` / `countdownEndsAt` are of the table a
 * newcomer would join (the open one with the most players), `count` — how many tables (absent on older servers = 1).
 */
export interface LobbyHongbao {
  tables: {
    room: HongbaoRoom;
    roundNo: number;
    phase: HongbaoPhase;
    /** players seated in the current rounds of the stake's tables */
    players: number;
    capacity: number;
    /** server ms of the start; null while waiting for players / not open */
    countdownEndsAt: number | null;
    /** tables of the stake */
    count?: number;
  }[];
}

export interface LobbyRoulette {
  roundNo: number;
  phase: RoulettePhase;
  /** server ms of the end of the current phase */
  phaseEndsAt: number;
  /** tokens staked per colour in the current round */
  staked: Record<RouletteBetColor, number>;
  /** last outcomes, newest first (≤ LOBBY_HISTORY) */
  last: RouletteBetColor[];
}

export const LOBBY_HISTORY = 12;

/** Recent big wins for the ticker (part of GET /api/lobby/live), newest first */
export interface LiveWin {
  betId: string;
  game: GameId;
  nickname: string;
  avatar: string;
  amount: number;
  payout: number;
  multiplier: number | null;
  at: string;
}

/**
 * GET /api/version — what build runs on the server (build-info.json, scripts/build-info.mjs).
 * In development there is no build-info.json: version from package.json, build / commit / builtAt are null
 * (the date is not invented — «now» is not a build date).
 */
export interface VersionInfo {
  version: string;
  build: number | null;
  /** short git sha */
  commit: string | null;
  /** ISO time the build-info was written; null = development */
  builtAt: string | null;
  env: 'development' | 'test' | 'production';
}

/** GET /api/health (+ the same build fields as /api/version) */
export interface HealthStatus extends VersionInfo {
  ok: boolean;
  db: boolean;
  redis: boolean | null;
  time: string;
}
